import { ReactNode } from "react";
import {
  Checkbox,
  CircularProgress,
  MenuItem,
  Pagination,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Theme,
} from "@mui/material";
import { commonStyles } from "../commonStyles";

// Shared manage-page table (platform UI/UX polish, Phase A). Presentational
// only — sorting/filtering/pagination COMPUTATION stays in the pages. Every
// default is a verbatim copy of what ManageMembers renders today; the other
// manage pages adopt in Phase B by overriding the escape-hatch props.

export type SortDirection = "asc" | "desc";

export type DataTableColumn<Row, ColKey extends string = string> = {
  key: ColKey;
  // ReactNode (Phase B additive widening, B4): lets decorative header content
  // — e.g. ManageInvoices' uncontrolled checkbox column — ride as a regular
  // column with byte-identical cell geometry. Existing callers pass strings;
  // zero-runtime change.
  header: ReactNode;
  // default false → plain header text (no TableSortLabel)
  sortable?: boolean;
  // e.g. "18%" → header TableCell sx {width}
  width?: string;
  // merged after width; ManageMembers' non-sortable Actions column passes
  // { fontWeight: 800, width: "10%" }
  headerCellSx?: SxProps<Theme>;
  // page-owned cell markup (keeps each page's cells byte-identical)
  render: (row: Row) => ReactNode;
  // optional per-column body-cell sx (Invoices/Events Phase B)
  cellSx?: SxProps<Theme>;
  // Keep an existing actions column visible while the rest of a wide manage
  // table scrolls horizontally. Callers opt in explicitly so ordinary data
  // columns and non-manage tables retain their current behavior.
  stickyRight?: boolean;
};

export type DataTableSort<ColKey extends string = string> = {
  column: ColKey;
  direction: SortDirection;
  // Toggle rules stay page-owned (Members: new column → asc; Emails: new
  // column → desc unless it is a text column).
  onRequestSort: (column: ColKey) => void;
};

// Phase B only (Events/Emails/Invoices leading checkbox column).
export type DataTableSelection<Row> = {
  isRowSelected: (row: Row) => boolean;
  onToggleRow: (row: Row) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  // indeterminate header checkbox (Events)
  someSelected?: boolean;
  // Default undefined → theme MuiCheckbox default (CMP gold per Q-008),
  // matching the sx-less checkboxes ManageEvents renders today.
  checkboxSx?: SxProps<Theme>;
  headerAriaLabel?: string;
  getRowAriaLabel?: (row: Row) => string;
};

// Phase B only (Events); renders INSIDE the container, after </Table>.
export type DataTablePagination = {
  // 1-based
  page: number;
  pageCount: number;
  pageSize: number;
  // default [10, 25, 50]
  pageSizeOptions?: number[];
  // post-filter count
  totalCount: number;
  // "events" → `Showing ${a} to ${b} of ${n} events` / "Showing 0 events"
  itemNoun: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  paginationSx?: SxProps<Theme>;
  pageSizeSelectSx?: SxProps<Theme>;
};

export type DataTableProps<Row, ColKey extends string = string> = {
  ariaLabel: string;
  columns: ReadonlyArray<DataTableColumn<Row, ColKey>>;
  rows: ReadonlyArray<Row>;
  getRowKey: (row: Row) => string;
  sort?: DataTableSort<ColKey>;
  // CALLER-computed booleans. Members gates them on DIFFERENT datasets —
  // loading on the unfiltered cache (manageMembersLoading &&
  // allMembers.length === 0), empty on the filtered rows
  // (!manageMembersLoading && members.length === 0) — so DataTable must NOT
  // derive them from rows + a loading flag (that would change behavior when
  // a refetch runs while filters exclude every cached row).
  showLoadingRow?: boolean;
  loadingRowContent?: ReactNode;
  showEmptyRow?: boolean;
  emptyRowContent?: ReactNode;
  containerClassName?: string;
  tableSx?: SxProps<Theme>;
  headClassName?: string;
  rowClassName?: string | ((row: Row) => string);
  sortLabelSx?: SxProps<Theme>;
  // Phase B escape hatch: Events' arrow-stack sort button, Emails'
  // UnfoldMoreIcon TableSortLabel.
  renderSortLabel?: (args: {
    column: ColKey;
    // mirrors DataTableColumn.header's ReactNode widening
    label: ReactNode;
    active: boolean;
    direction: SortDirection;
    onRequestSort: (column: ColKey) => void;
  }) => ReactNode;
  // omit in Phase A
  selection?: DataTableSelection<Row>;
  // omit in Phase A
  pagination?: DataTablePagination;
};

// Verbatim ManageMembers table sx.
export const dataTableSx = {
  minWidth: 1120,
  tableLayout: "fixed",
  width: "100%",
  "& .MuiTableCell-root": {
    borderColor: "#E5E7EB",
    overflowWrap: "anywhere",
    px: 1.5,
    py: 2.5,
    verticalAlign: "middle",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    overflowWrap: "normal",
    whiteSpace: "nowrap",
  },
};

// Verbatim ManageMembers TableSortLabel sx.
export const sortLabelSx = {
  alignItems: "center",
  display: "inline-flex",
  fontWeight: 800,
  lineHeight: 1.2,
  maxWidth: "100%",
  whiteSpace: "nowrap",
  "& .MuiTableSortLabel-icon": {
    flexShrink: 0,
    marginLeft: 0.5,
  },
};

// Shared opaque chrome for fixed-right manage-table action columns. The
// background prevents scrolled data from showing through, while the divider
// and shadow make the frozen boundary clear without changing row content.
export const stickyActionColumnHeaderSx = {
  backgroundClip: "padding-box",
  backgroundColor: "#F9FAFB",
  borderLeft: "1px solid #E5E7EB",
  boxShadow: "-8px 0 12px -10px rgba(15, 23, 42, 0.35)",
  position: "sticky",
  right: 0,
  zIndex: 3,
} as const;

export const stickyActionColumnCellSx = {
  backgroundClip: "padding-box",
  backgroundColor: "#FFFFFF",
  borderLeft: "1px solid #E5E7EB",
  boxShadow: "-8px 0 12px -10px rgba(15, 23, 42, 0.35)",
  position: "sticky",
  right: 0,
  zIndex: 1,
} as const;

// Verbatim ManageEvents pagination sx (Phase B default).
export const dataTablePaginationSx = {
  "& .MuiPaginationItem-root": {
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    color: "#111827",
    fontWeight: 600,
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: "white",
    border: `1px solid ${commonStyles.cpaGold}`,
    color: commonStyles.cpaGold,
    "&:hover": { backgroundColor: "white" },
  },
  "& .MuiPaginationItem-ellipsis": {
    border: "none",
  },
};

// Verbatim ManageEvents page-size select sx (Phase B default).
export const dataTablePageSizeSelectSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: 14,
    fontWeight: 600,
    "& fieldset": { borderColor: "#E5E7EB" },
  },
};

// Verbatim ManageMembers loading/empty rows (Phase A rule: shared defaults
// ARE ManageMembers' current values; other pages override in Phase B).
const defaultLoadingRowContent = (
  <div className="flex items-center justify-center gap-3 py-8 text-sm font-semibold text-gray-500">
    <CircularProgress size={20} />
    Loading members...
  </div>
);

const defaultEmptyRowContent = (
  <div className="py-8 text-center text-sm font-semibold text-gray-500">
    No members match the current search and filters.
  </div>
);

const getHeaderCellSx = <Row, ColKey extends string>(
  column: DataTableColumn<Row, ColKey>
): SxProps<Theme> | undefined => {
  const styles: SxProps<Theme>[] = [];

  if (column.width) {
    styles.push({ width: column.width });
  }

  if (column.headerCellSx) {
    styles.push(
      ...(Array.isArray(column.headerCellSx)
        ? column.headerCellSx
        : [column.headerCellSx])
    );
  }

  if (column.stickyRight) {
    styles.push(stickyActionColumnHeaderSx);
  }

  return styles.length > 0 ? (styles as SxProps<Theme>) : undefined;
};

const getBodyCellSx = <Row, ColKey extends string>(
  column: DataTableColumn<Row, ColKey>
): SxProps<Theme> | undefined => {
  if (!column.stickyRight) {
    return column.cellSx;
  }

  const styles = column.cellSx
    ? Array.isArray(column.cellSx)
      ? column.cellSx
      : [column.cellSx]
    : [];

  return [...styles, stickyActionColumnCellSx] as SxProps<Theme>;
};

export function DataTable<Row, ColKey extends string = string>({
  ariaLabel,
  columns,
  rows,
  getRowKey,
  sort,
  showLoadingRow = false,
  loadingRowContent = defaultLoadingRowContent,
  showEmptyRow = false,
  emptyRowContent = defaultEmptyRowContent,
  containerClassName = "mt-5 w-full overflow-x-auto rounded-lg border border-gray-300 bg-white",
  tableSx = dataTableSx,
  headClassName = "bg-gray-50",
  rowClassName = "hover:bg-gray-50",
  sortLabelSx: sortLabelSxProp = sortLabelSx,
  renderSortLabel,
  selection,
  pagination,
}: DataTableProps<Row, ColKey>) {
  const messageRowColSpan = columns.length + (selection ? 1 : 0);
  const pageSizeOptions = pagination?.pageSizeOptions ?? [10, 25, 50];

  return (
    <TableContainer className={containerClassName}>
      <Table aria-label={ariaLabel} sx={tableSx}>
        <TableHead className={headClassName}>
          <TableRow>
            {selection && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selection.allSelected}
                  indeterminate={
                    !selection.allSelected && Boolean(selection.someSelected)
                  }
                  onChange={selection.onToggleAll}
                  inputProps={{ "aria-label": selection.headerAriaLabel }}
                  sx={selection.checkboxSx}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell key={column.key} sx={getHeaderCellSx(column)}>
                {column.sortable && sort ? (
                  renderSortLabel ? (
                    renderSortLabel({
                      column: column.key,
                      label: column.header,
                      active: sort.column === column.key,
                      direction: sort.direction,
                      onRequestSort: sort.onRequestSort,
                    })
                  ) : (
                    <TableSortLabel
                      active={sort.column === column.key}
                      direction={
                        sort.column === column.key ? sort.direction : "asc"
                      }
                      onClick={() => sort.onRequestSort(column.key)}
                      sx={sortLabelSxProp}
                    >
                      {column.header}
                    </TableSortLabel>
                  )
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {showLoadingRow && (
            <TableRow>
              <TableCell colSpan={messageRowColSpan}>
                {loadingRowContent}
              </TableCell>
            </TableRow>
          )}
          {showEmptyRow && (
            <TableRow>
              <TableCell colSpan={messageRowColSpan}>
                {emptyRowContent}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              key={getRowKey(row)}
              className={
                typeof rowClassName === "function"
                  ? rowClassName(row)
                  : rowClassName
              }
            >
              {selection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selection.isRowSelected(row)}
                    onChange={() => selection.onToggleRow(row)}
                    inputProps={{
                      "aria-label": selection.getRowAriaLabel?.(row),
                    }}
                    sx={selection.checkboxSx}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell key={column.key} sx={getBodyCellSx(column)}>
                  {column.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pagination && (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row">
          <p className="text-sm text-gray-600">
            {pagination.totalCount === 0
              ? `Showing 0 ${pagination.itemNoun}`
              : `Showing ${
                  (pagination.page - 1) * pagination.pageSize + 1
                } to ${Math.min(
                  pagination.page * pagination.pageSize,
                  pagination.totalCount
                )} of ${pagination.totalCount} ${pagination.itemNoun}`}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Pagination
              count={pagination.pageCount}
              page={pagination.page}
              onChange={(_, value) => pagination.onPageChange(value)}
              shape="rounded"
              sx={pagination.paginationSx ?? dataTablePaginationSx}
            />
            <TextField
              select
              size="small"
              value={pagination.pageSize}
              onChange={({ target }) =>
                pagination.onPageSizeChange(Number(target.value))
              }
              sx={pagination.pageSizeSelectSx ?? dataTablePageSizeSelectSx}
              SelectProps={{
                renderValue: (selected) => `${selected} / page`,
              }}
              inputProps={{ "aria-label": `${pagination.itemNoun} per page` }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size} / page
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
      )}
    </TableContainer>
  );
}
